'use client'

import { useEffect, useRef } from 'react'
import { isNearBottom } from '../lib/chat-scroll'
import { ScrollArea } from './scroll-area'
import type { ReactNode } from 'react'

// ChatTranscript owns the scroll behaviour shared by every agent chat: an instant jump to the bottom
// when the conversation identity changes (resetKey), and a smooth follow on new content (followKey)
// but only when the user is already near the bottom, so it never yanks someone reading history.
export function ChatTranscript({
  children,
  resetKey,
  followKey
}: {
  children: ReactNode
  resetKey?: string | number
  followKey?: string | number
}) {
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'instant' })
  }, [resetKey])

  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-slot="scroll-area-viewport"]')
    if (!viewport) return
    const near = isNearBottom({
      scrollHeight: viewport.scrollHeight,
      scrollTop: viewport.scrollTop,
      clientHeight: viewport.clientHeight
    })
    if (near) bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [followKey])

  return (
    <ScrollArea ref={scrollAreaRef} className="min-h-0 flex-1">
      <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
        {children}
        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  )
}
